<template>
  <v-dialog
    :value="value"
    max-width="780"
    scrollable
    persistent
    :fullscreen="$vuetify.breakpoint.xsOnly"
    content-class="tt-dialog-modern"
    @input="$emit('input', $event)"
  >
    <v-card class="tt-card">
      <!-- HEADER -->
      <div class="tt-header">
        <div class="tt-header-icon">
          <v-icon size="27" color="#018c74"> mdi-store-plus-outline </v-icon>
        </div>

        <div class="tt-header-content">
          <div class="tt-eyebrow">KONTRIBUSI PUBLIK</div>
          <div class="tt-title">
            {{ berhasil ? "Pengajuan berhasil" : "Tambah Tempat Baru" }}
          </div>
          <div class="tt-subtitle">
            {{
              berhasil
                ? "Terima kasih telah membantu melengkapi informasi tempat."
                : "Bagikan informasi tempat menarik di sekitar Anda."
            }}
          </div>
        </div>

        <v-btn
          icon
          class="tt-close"
          :disabled="loading"
          aria-label="Tutup dialog"
          @click="tutup"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-divider class="tt-divider" />

      <!-- ================= SUKSES ================= -->
      <v-card-text v-if="berhasil" class="tt-success">
        <div class="tt-success-illustration">
          <div class="tt-success-circle tt-success-circle-outer"></div>
          <div class="tt-success-circle tt-success-circle-inner"></div>
          <v-icon size="62" color="#018c74"> mdi-check-circle </v-icon>
        </div>

        <div class="tt-success-badge">
          <v-icon size="15" left>mdi-check-decagram-outline</v-icon>
          DATA BERHASIL DIKIRIM
        </div>

        <h2 class="tt-success-title">Terima kasih atas kontribusi Anda!</h2>

        <p class="tt-success-text">
          Informasi tempat yang Anda kirim telah diterima dan sedang menunggu verifikasi
          admin. Setelah diperiksa dan disetujui, tempat ini dapat ditampilkan pada daftar
          informasi dan peta.
        </p>

        <div class="tt-status-card">
          <div class="tt-status-icon">
            <v-icon color="#018c74">mdi-clock-check-outline</v-icon>
          </div>
          <div>
            <div class="tt-status-title">Menunggu verifikasi</div>
            <div class="tt-status-caption">
              Admin akan memeriksa kelengkapan data terlebih dahulu.
            </div>
          </div>
        </div>

        <v-btn
          depressed
          color="#018c74"
          class="tt-primary-btn tt-success-button"
          @click="tutup"
        >
          <v-icon left small>mdi-check</v-icon>
          Selesai
        </v-btn>
      </v-card-text>

      <!-- ================= FORM ================= -->
      <v-card-text v-else class="tt-body">
        <v-form ref="form" @submit.prevent="kirim">
          <!-- Honeypot anti-bot -->
          <input
            v-model="form.website"
            type="text"
            tabindex="-1"
            autocomplete="off"
            class="tt-honeypot"
            aria-hidden="true"
          />

          <!-- INFO BANTUAN -->
          <div class="tt-info-banner">
            <div class="tt-info-icon">
              <v-icon color="#018c74" size="21"> mdi-information-outline </v-icon>
            </div>
            <div class="tt-info-content">
              <div class="tt-info-title">Sebelum mulai</div>
              <div class="tt-info-text">
                Lengkapi informasi yang Anda ketahui. Kolom bertanda
                <strong>*</strong> wajib diisi. Admin dapat memeriksa dan mengoreksi data
                sebelum disetujui.
              </div>
            </div>
          </div>

          <!-- BAGIAN 1: INFORMASI TEMPAT -->
          <div class="tt-section">
            <div class="tt-section-heading">
              <div class="tt-section-icon">
                <v-icon color="#018c74" size="21"> mdi-storefront-outline </v-icon>
              </div>
              <div class="tt-section-content">
                <div class="tt-section-title">Informasi tempat</div>
                <div class="tt-section-description">
                  Masukkan identitas dan detail tempat yang diajukan.
                </div>
              </div>
              <span class="tt-section-number">01</span>
            </div>

            <v-row dense class="tt-form-grid">
              <v-col cols="12" md="6">
                <v-autocomplete
                  v-model="form.sub_category_id"
                  :items="subCategories"
                  item-text="nama_sub"
                  item-value="id"
                  label="Kategori tempat *"
                  placeholder="Pilih kategori"
                  outlined
                  dense
                  hide-details="auto"
                  color="#018c74"
                  background-color="#ffffff"
                  prepend-inner-icon="mdi-shape-outline"
                  no-data-text="Kategori tidak ditemukan"
                  :rules="[(v) => !!v || 'Pilih kategori tempat']"
                />
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.nama_profil"
                  label="Nama tempat *"
                  placeholder="Contoh: Wisata Kawah Ijen"
                  outlined
                  dense
                  hide-details="auto"
                  color="#018c74"
                  background-color="#ffffff"
                  counter="255"
                  maxlength="255"
                  prepend-inner-icon="mdi-storefront-outline"
                  :rules="[
                    (v) => !!(v && v.trim()) || 'Nama wajib diisi',
                    (v) => (v || '').trim().length >= 3 || 'Minimal 3 karakter',
                  ]"
                />
              </v-col>

              <v-col cols="12">
                <v-textarea
                  v-model="form.alamat_lengkap"
                  label="Alamat lengkap"
                  placeholder="Nama jalan, desa/kelurahan, kecamatan, dan patokan lokasi..."
                  outlined
                  dense
                  rows="2"
                  auto-grow
                  hide-details="auto"
                  color="#018c74"
                  background-color="#ffffff"
                  prepend-inner-icon="mdi-map-marker-outline"
                />
              </v-col>

              <v-col cols="12">
                <v-textarea
                  v-model="form.deskripsi"
                  label="Deskripsi tempat"
                  placeholder="Ceritakan secara singkat tentang tempat ini, fasilitas, atau daya tariknya..."
                  outlined
                  dense
                  rows="3"
                  auto-grow
                  hide-details="auto"
                  color="#018c74"
                  background-color="#ffffff"
                  prepend-inner-icon="mdi-text-box-outline"
                />
              </v-col>
            </v-row>
          </div>

          <v-divider class="tt-section-divider" />

          <!-- BAGIAN 2: KONTAK -->
          <div class="tt-section">
            <div class="tt-section-heading">
              <div class="tt-section-icon">
                <v-icon color="#018c74" size="21"> mdi-link-variant </v-icon>
              </div>
              <div class="tt-section-content">
                <div class="tt-section-title">Kontak dan tautan</div>
                <div class="tt-section-description">
                  Tambahkan informasi yang bisa digunakan pengunjung.
                </div>
              </div>
              <span class="tt-section-number">02</span>
            </div>

            <v-row dense class="tt-form-grid">
              <v-col cols="12" md="6">
                <v-text-field
                  :value="form.kontak"
                  label="Nomor telepon / WhatsApp"
                  placeholder="628xxxxxxxxxx"
                  hint="Awalan 0 otomatis menjadi 62"
                  outlined
                  dense
                  hide-details="auto"
                  color="#018c74"
                  background-color="#ffffff"
                  inputmode="tel"
                  prepend-inner-icon="mdi-phone-outline"
                  :rules="teleponRules"
                  @input="(v) => aturTelepon('kontak', v)"
                  @focus="isiAwalanTelepon('kontak')"
                  @blur="rapikanTelepon('kontak')"
                />
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.link"
                  label="Website / Instagram / Maps (opsional)"
                  placeholder="https://... atau isi - bila tidak ada"
                  outlined
                  dense
                  hide-details="auto"
                  color="#018c74"
                  background-color="#ffffff"
                  prepend-inner-icon="mdi-open-in-new"
                  :rules="linkRules"
                />
              </v-col>
            </v-row>
          </div>

          <v-divider class="tt-section-divider" />

          <!-- BAGIAN 3: LOKASI -->
          <div class="tt-section">
            <div class="tt-section-heading">
              <div class="tt-section-icon">
                <v-icon color="#018c74" size="21"> mdi-map-marker-radius-outline </v-icon>
              </div>
              <div class="tt-section-content">
                <div class="tt-section-title">Tentukan lokasi</div>
                <div class="tt-section-description">
                  Pilih titik lokasi tempat pada peta di bawah ini.
                </div>
              </div>
              <span class="tt-section-number">03</span>
            </div>

            <div class="tt-map-card">
              <div class="tt-map-toolbar">
                <div class="tt-map-toolbar-info">
                  <div class="tt-map-toolbar-title">
                    <v-icon size="18" color="#018c74" class="mr-1">
                      mdi-map-outline
                    </v-icon>
                    Lokasi pada peta
                    <span class="tt-required">*</span>
                  </div>
                  <div class="tt-map-toolbar-caption">
                    Klik peta untuk menentukan titik tempat.
                  </div>
                </div>

                <v-btn
                  small
                  depressed
                  color="#018c74"
                  class="tt-location-btn"
                  :loading="mencariLokasi"
                  @click="gunakanLokasiSaya"
                >
                  <v-icon left small>mdi-crosshairs-gps</v-icon>
                  Lokasi saya
                </v-btn>
              </div>

              <div class="tt-map-wrap" :class="{ 'tt-map-error': tampilkanErrorLokasi }">
                <div :id="mapId" class="tt-map"></div>

                <div v-if="!lokasiDipilih" class="tt-map-overlay-hint">
                  <div class="tt-map-hint-icon">
                    <v-icon color="#018c74" size="22">
                      mdi-map-marker-plus-outline
                    </v-icon>
                  </div>
                  <span>Pilih titik lokasi pada peta</span>
                </div>
              </div>

              <div class="tt-map-footer">
                <div
                  class="tt-coordinate-icon"
                  :class="{ 'tt-coordinate-active': lokasiDipilih }"
                >
                  <v-icon size="18" color="#018c74">
                    {{ lokasiDipilih ? "mdi-check-circle-outline" : "mdi-crosshairs" }}
                  </v-icon>
                </div>

                <div class="tt-coordinate-content">
                  <div class="tt-coordinate-title">
                    {{ lokasiDipilih ? "Koordinat terpilih" : "Belum ada lokasi" }}
                  </div>
                  <div class="tt-coordinate-value">
                    {{
                      lokasiDipilih
                        ? form.lokasi_map
                        : "Klik peta atau gunakan lokasi perangkat Anda."
                    }}
                  </div>
                </div>

                <v-icon v-if="lokasiDipilih" color="#018c74" size="21">
                  mdi-check-decagram
                </v-icon>
              </div>
            </div>

            <div v-if="tampilkanErrorLokasi" class="tt-error">
              <v-icon color="#d9534f" size="15" class="mr-1">
                mdi-alert-circle-outline
              </v-icon>
              Tentukan lokasi tempat pada peta terlebih dahulu.
            </div>
          </div>

          <v-divider class="tt-section-divider" />

          <!-- BAGIAN 4: FOTO -->
          <div class="tt-section">
            <div class="tt-section-heading">
              <div class="tt-section-icon">
                <v-icon color="#018c74" size="21"> mdi-image-multiple-outline </v-icon>
              </div>
              <div class="tt-section-content">
                <div class="tt-section-title">Foto tempat</div>
                <div class="tt-section-description">
                  Foto membantu pengunjung mengenali tempat yang diajukan.
                </div>
              </div>
              <span class="tt-section-number">04</span>
            </div>

            <div class="tt-upload-area">
              <v-file-input
                v-model="fotoFiles"
                label="Pilih foto tempat"
                placeholder="Pilih maksimal 5 foto"
                outlined
                dense
                multiple
                chips
                small-chips
                show-size
                counter
                accept="image/jpeg,image/png,image/webp"
                color="#018c74"
                prepend-icon=""
                prepend-inner-icon="mdi-camera-plus-outline"
                hide-details="auto"
                :rules="fotoRules"
                @change="buatPreview"
              />

              <div class="tt-upload-note">
                <v-icon size="15" color="#018c74" class="mr-1">
                  mdi-information-outline
                </v-icon>
                Format JPG, PNG, atau WEBP. Maksimal 5 foto.
              </div>

              <div v-if="previews.length" class="tt-preview-container">
                <div class="tt-preview-heading">
                  <span>Pratinjau foto</span>
                  <span class="tt-preview-count"> {{ previews.length }} / 5 </span>
                </div>

                <div class="tt-preview-row">
                  <div v-for="(p, i) in previews" :key="p" class="tt-preview">
                    <img :src="p" :alt="'Foto tempat ' + (i + 1)" />

                    <div class="tt-preview-index">
                      {{ i + 1 }}
                    </div>

                    <button
                      type="button"
                      class="tt-preview-remove"
                      :aria-label="'Hapus foto ' + (i + 1)"
                      @click="hapusFoto(i)"
                    >
                      <v-icon color="#ffffff" size="16"> mdi-close </v-icon>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <v-divider class="tt-section-divider" />

          <!-- BAGIAN 5: PENGIRIM -->
          <div class="tt-section">
            <div class="tt-section-heading">
              <div class="tt-section-icon">
                <v-icon color="#018c74" size="21"> mdi-account-outline </v-icon>
              </div>
              <div class="tt-section-content">
                <div class="tt-section-title">Informasi pengirim</div>
                <div class="tt-section-description">
                  Opsional, untuk memudahkan admin mengonfirmasi data.
                </div>
              </div>
              <span class="tt-section-number">05</span>
            </div>

            <div class="tt-sender-note">
              <v-icon color="#018c74" size="18" class="mr-2">
                mdi-shield-check-outline
              </v-icon>
              <span>
                Informasi pengirim digunakan untuk keperluan konfirmasi oleh admin.
              </span>
            </div>

            <v-row dense class="tt-form-grid">
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.nama_pengirim"
                  label="Nama Anda"
                  placeholder="Masukkan nama"
                  outlined
                  dense
                  hide-details="auto"
                  color="#018c74"
                  background-color="#ffffff"
                  maxlength="100"
                  prepend-inner-icon="mdi-account-outline"
                />
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  :value="form.kontak_pengirim"
                  label="Nomor HP Anda"
                  placeholder="628xxxxxxxxxx"
                  hint="Awalan 0 otomatis menjadi 62"
                  outlined
                  dense
                  hide-details="auto"
                  color="#018c74"
                  background-color="#ffffff"
                  inputmode="tel"
                  prepend-inner-icon="mdi-cellphone"
                  :rules="teleponRules"
                  @input="(v) => aturTelepon('kontak_pengirim', v)"
                  @focus="isiAwalanTelepon('kontak_pengirim')"
                  @blur="rapikanTelepon('kontak_pengirim')"
                />
              </v-col>
            </v-row>
          </div>

          <!-- ERROR -->
          <v-alert v-if="pesanError" dense text type="error" class="tt-error-alert">
            {{ pesanError }}
          </v-alert>
        </v-form>
      </v-card-text>

      <!-- FOOTER -->
      <template v-if="!berhasil">
        <v-divider class="tt-divider" />

        <div class="tt-footer">
          <div class="tt-footer-note">
            <v-icon size="17" color="#018c74" class="mr-1">
              mdi-shield-check-outline
            </v-icon>
            <span>Data akan diverifikasi admin.</span>
          </div>

          <div class="tt-footer-actions">
            <v-btn text class="tt-cancel-btn" :disabled="loading" @click="tutup">
              Batal
            </v-btn>

            <v-btn
              depressed
              color="#018c74"
              class="tt-primary-btn"
              :loading="loading"
              @click="kirim"
            >
              <v-icon left small>mdi-send-outline</v-icon>
              Kirim Pengajuan
            </v-btn>
          </div>
        </div>
      </template>
    </v-card>
  </v-dialog>
</template>

<script>
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { ajukanTenantBaru } from "@/api/tenantPublikApi";

const PUSAT_BONDOWOSO = [-7.9135, 113.8217];

const TIPE_FOTO = ["image/jpeg", "image/png", "image/webp"];

const FIELD_TELEPON = ["kontak", "kontak_pengirim"];

/**
 * Ubah input nomor telepon menjadi format 62xxxxxxxxxx.
 * - Hanya angka yang disimpan (spasi, tanda -, +, dan huruf dibuang)
 * - 0812...   -> 62812...
 * - +62812... -> 62812...
 * - 812...    -> 62812...
 * - 620812... -> 62812...  (angka 0 setelah 62 dibuang)
 * - Maksimal 15 digit
 */
const formatTelepon = (nilai) => {
  let digit = String(nilai || "").replace(/\D/g, "");
  if (!digit) return "";

  // Ketikan awal "6" dibiarkan, menunggu angka "2" berikutnya.
  if (digit === "6") return "6";

  if (digit.startsWith("0")) {
    digit = "62" + digit.replace(/^0+/, "");
  } else if (!digit.startsWith("62")) {
    digit = "62" + digit;
  }

  // Buang angka 0 yang tertinggal setelah awalan 62.
  digit = "62" + digit.slice(2).replace(/^0+/, "");

  return digit.slice(0, 15);
};

const formKosong = (subId) => ({
  sub_category_id: subId || null,
  nama_profil: "",
  deskripsi: "",
  alamat_lengkap: "",
  kontak: "",
  link: "",
  lokasi_map: "",
  nama_pengirim: "",
  kontak_pengirim: "",
  website: "",
});

export default {
  name: "TenantTambahDialog",

  props: {
    value: { type: Boolean, default: false },
    subCategories: { type: Array, default: () => [] },
    defaultSubId: { type: [Number, String], default: null },
  },

  data() {
    return {
      mapId: "map-tenant-publik",
      map: null,
      marker: null,

      form: formKosong(this.defaultSubId ? Number(this.defaultSubId) : null),

      fotoFiles: [],
      previews: [],

      loading: false,
      berhasil: false,
      pesanError: "",
      mencariLokasi: false,
      sudahCobaKirim: false,

      // Link opsional: boleh kosong, boleh "-", atau harus diawali http(s)://
      linkRules: [
        (v) => {
          const nilai = (v || "").trim();
          if (nilai === "" || nilai === "-") return true;
          return (
            /^https?:\/\//i.test(nilai) ||
            "Gunakan awalan http:// atau https://, atau isi - bila tidak ada"
          );
        },
      ],

      // Telepon opsional: kosong (atau hanya "62") dianggap tidak diisi.
      // Kalau diisi: 62 + 8 sampai 13 digit (total 10-15 digit).
      teleponRules: [
        (v) => {
          const nilai = (v || "").trim();
          if (nilai === "" || nilai === "62") return true;
          return /^62\d{8,13}$/.test(nilai) || "Nomor tidak valid, contoh: 6281234567890";
        },
      ],

      fotoRules: [
        (v) => !v || v.length <= 5 || "Maksimal 5 foto",
        (v) =>
          !v ||
          v.every((f) => TIPE_FOTO.includes(f.type)) ||
          "Gunakan foto JPG, PNG, atau WEBP",
      ],
    };
  },

  computed: {
    lokasiDipilih() {
      return !!this.form.lokasi_map;
    },

    tampilkanErrorLokasi() {
      return this.sudahCobaKirim && !this.lokasiDipilih;
    },
  },

  watch: {
    value(buka) {
      if (buka) {
        this.reset();

        this.$nextTick(() => {
          setTimeout(() => {
            if (this.value) this.initMap();
          }, 350);
        });
      } else {
        this.hancurkanMap();
      }
    },

    defaultSubId() {
      if (!this.value) {
        this.form.sub_category_id = this.defaultSubId ? Number(this.defaultSubId) : null;
      }
    },
  },

  beforeDestroy() {
    this.hancurkanMap();
    this.bersihkanPreview();
  },

  methods: {
    reset() {
      this.form = formKosong(this.defaultSubId ? Number(this.defaultSubId) : null);

      this.fotoFiles = [];
      this.bersihkanPreview();

      this.berhasil = false;
      this.pesanError = "";
      this.sudahCobaKirim = false;

      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },

    tutup() {
      if (this.loading) return;
      this.$emit("input", false);
    },

    // ================= TELEPON =================

    // Dipanggil setiap kali pengguna mengetik atau menempel nomor.
    aturTelepon(field, nilai) {
      const rapi = formatTelepon(nilai);

      // Set nilai mentah dulu, lalu nilai rapi di tick berikutnya, supaya
      // tampilan input ikut diperbarui walau hasil format sama dengan sebelumnya
      // (misalnya saat pengguna mengetik huruf).
      this.form[field] = nilai;
      this.$nextTick(() => {
        this.form[field] = rapi;
      });
    },

    // Saat kolom difokuskan dalam keadaan kosong, tampilkan awalan 62.
    isiAwalanTelepon(field) {
      if (!this.form[field]) {
        this.form[field] = "62";
      }
    },

    // Saat kolom ditinggalkan dan hanya berisi "62", kosongkan lagi.
    rapikanTelepon(field) {
      if (this.form[field] === "62") {
        this.form[field] = "";
      }
    },

    // ================= PETA =================

    initMap() {
      const el = document.getElementById(this.mapId);
      if (!el) return;

      this.hancurkanMap();

      this.map = L.map(el, {
        attributionControl: true,
        tap: false,
      }).setView(PUSAT_BONDOWOSO, 12);

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        maxZoom: 19,
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      }).addTo(this.map);

      this.map.on("click", (e) => {
        this.pasangMarker(e.latlng.lat, e.latlng.lng);
      });

      // Memulihkan marker apabila lokasi sudah terisi.
      if (this.form.lokasi_map) {
        const koordinat = this.form.lokasi_map.split(",").map(Number);

        if (koordinat.length === 2 && koordinat.every(Number.isFinite)) {
          this.pasangMarker(koordinat[0], koordinat[1]);
        }
      }

      setTimeout(() => {
        if (this.map) this.map.invalidateSize();
      }, 150);
    },

    hancurkanMap() {
      if (this.map) {
        this.map.off();
        this.map.remove();
        this.map = null;
        this.marker = null;
      }
    },

    pasangMarker(lat, lng, zoom = null) {
      if (!this.map) return;

      const latlng = [lat, lng];

      if (!this.marker) {
        this.marker = L.marker(latlng, {
          draggable: true,
          icon: L.divIcon({
            className: "tt-pin",
            html: '<div class="tt-pin-dot"><span></span></div>',
            iconSize: [28, 36],
            iconAnchor: [14, 32],
          }),
        }).addTo(this.map);

        this.marker.on("dragend", () => {
          const p = this.marker.getLatLng();

          this.form.lokasi_map = `${p.lat.toFixed(6)},${p.lng.toFixed(6)}`;
        });
      } else {
        this.marker.setLatLng(latlng);
      }

      this.form.lokasi_map = `${lat.toFixed(6)},${lng.toFixed(6)}`;

      if (zoom) {
        this.map.setView(latlng, zoom);
      }

      this.pesanError = "";
    },

    gunakanLokasiSaya() {
      if (!navigator.geolocation) {
        this.pesanError = "Perangkat Anda tidak mendukung pencarian lokasi.";
        return;
      }

      this.mencariLokasi = true;
      this.pesanError = "";

      navigator.geolocation.getCurrentPosition(
        (pos) => {
          this.mencariLokasi = false;

          this.pasangMarker(pos.coords.latitude, pos.coords.longitude, 17);
        },
        (error) => {
          this.mencariLokasi = false;

          if (error.code === 1) {
            this.pesanError =
              "Izin lokasi ditolak. Izinkan akses lokasi atau pilih langsung pada peta.";
          } else if (error.code === 3) {
            this.pesanError =
              "Pencarian lokasi terlalu lama. Coba lagi atau pilih titik pada peta.";
          } else {
            this.pesanError =
              "Lokasi tidak dapat diakses. Periksa pengaturan perangkat Anda.";
          }
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      );
    },

    // ================= FOTO =================

    buatPreview(files) {
      this.bersihkanPreview();

      const daftar = Array.isArray(files) ? files : [];

      if (daftar.length > 5) {
        this.fotoFiles = daftar.slice(0, 5);
      }

      this.previews = this.fotoFiles.map((file) => URL.createObjectURL(file));
    },

    hapusFoto(index) {
      const preview = this.previews[index];

      if (preview) {
        URL.revokeObjectURL(preview);
      }

      this.fotoFiles = this.fotoFiles.filter((_, i) => i !== index);

      this.previews = this.previews.filter((_, i) => i !== index);
    },

    bersihkanPreview() {
      this.previews.forEach((url) => URL.revokeObjectURL(url));
      this.previews = [];
    },

    // ================= KIRIM =================

    async kirim() {
      if (this.loading) return;

      this.sudahCobaKirim = true;
      this.pesanError = "";

      // Nomor yang hanya berisi awalan "62" dianggap kosong.
      FIELD_TELEPON.forEach((field) => {
        if (this.form[field] === "62") this.form[field] = "";
      });

      const formValid = this.$refs.form.validate();

      if (!formValid || !this.lokasiDipilih) {
        this.pesanError = !this.lokasiDipilih
          ? "Lengkapi data yang bertanda * dan tentukan lokasi pada peta."
          : "Periksa kembali data yang Anda masukkan.";

        return;
      }

      // Honeypot: abaikan pengiriman yang mengisi kolom tersembunyi.
      if (this.form.website) return;

      // Rapikan link: buang spasi di awal/akhir (kosong atau "-" tetap diperbolehkan).
      this.form.link = (this.form.link || "").trim();

      this.loading = true;

      try {
        const hasil = await ajukanTenantBaru(this.form, this.fotoFiles || []);

        if (hasil && hasil.ok) {
          this.berhasil = true;
          this.$emit("terkirim");
        } else {
          this.pesanError =
            (hasil && hasil.message) ||
            "Pengajuan belum berhasil dikirim. Silakan coba kembali.";
        }
      } catch (error) {
        this.pesanError =
          "Terjadi kesalahan saat mengirim data. Periksa koneksi Anda dan coba kembali.";
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
/* ================= WARNA & KARTU ================= */

.tt-card {
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  overflow: hidden;
  border-radius: 20px !important;
  background: #ffffff;
  box-shadow: 0 24px 70px rgba(26, 58, 50, 0.15) !important;
}

.tt-divider {
  border-color: #edf1ef !important;
}

/* ================= HEADER ================= */

.tt-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 22px 24px;
  background: linear-gradient(120deg, #f0f9f6 0%, #ffffff 75%);
  flex-shrink: 0;
}

.tt-header-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 52px;
  height: 52px;
  border: 1px solid rgba(1, 140, 116, 0.12);
  border-radius: 16px;
  background: #e2f3ee;
}

.tt-header-content {
  flex: 1;
  min-width: 0;
}

.tt-eyebrow {
  margin-bottom: 4px;
  color: #018c74;
  font-size: 0.65rem;
  font-weight: 800;
  letter-spacing: 1.4px;
}

.tt-title {
  color: #203b35;
  font-size: 1.22rem;
  font-weight: 800;
  line-height: 1.4;
}

.tt-subtitle {
  margin-top: 4px;
  color: #72817c;
  font-size: 0.79rem;
  line-height: 1.5;
}

.tt-close {
  align-self: flex-start;
  color: #64746e !important;
}

/* ================= BODY ================= */

.tt-body {
  flex: 1 1 auto;
  min-height: 0;
  padding: 22px 26px 26px !important;
  overflow-y: auto;
}

.tt-body::-webkit-scrollbar {
  width: 6px;
}

.tt-body::-webkit-scrollbar-thumb {
  border-radius: 10px;
  background: #dce8e3;
}

.tt-body::-webkit-scrollbar-track {
  background: transparent;
}

/* ================= INFO BANNER ================= */

.tt-info-banner {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 15px 16px;
  margin-bottom: 27px;
  border: 1px solid #d9eee7;
  border-radius: 13px;
  background: #f3faf7;
}

.tt-info-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: #e1f3ed;
}

.tt-info-content {
  flex: 1;
}

.tt-info-title {
  margin-bottom: 4px;
  color: #24594b;
  font-size: 0.83rem;
  font-weight: 800;
}

.tt-info-text {
  color: #667a72;
  font-size: 0.76rem;
  line-height: 1.65;
}

/* ================= SECTION ================= */

.tt-section {
  padding: 0;
}

.tt-section-heading {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
}

.tt-section-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 42px;
  height: 42px;
  border: 1px solid #e0f0ea;
  border-radius: 12px;
  background: #f0f8f5;
}

.tt-section-content {
  flex: 1;
  min-width: 0;
}

.tt-section-title {
  color: #243b34;
  font-size: 0.94rem;
  font-weight: 800;
}

.tt-section-description {
  margin-top: 3px;
  color: #87928d;
  font-size: 0.74rem;
  line-height: 1.5;
}

.tt-section-number {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border: 1px solid #e4efea;
  border-radius: 10px;
  background: #fafcfb;
  color: #018c74;
  font-size: 0.72rem;
  font-weight: 800;
}

.tt-form-grid {
  margin-top: 0;
}

.tt-section-divider {
  margin: 27px 0;
  border-color: #edf1ef !important;
}

/* ================= INPUT ================= */

::v-deep .v-text-field--outlined fieldset {
  border-color: #dfe8e3;
  border-radius: 11px;
}

::v-deep .v-text-field--outlined:hover fieldset {
  border-color: #a9d7c8;
}

::v-deep .v-text-field--outlined.v-input--is-focused fieldset {
  border-color: #018c74 !important;
  border-width: 1.5px;
}

::v-deep .v-text-field .v-label {
  color: #788780;
  font-size: 0.82rem;
}

::v-deep .v-text-field input,
::v-deep .v-textarea textarea {
  color: #344740;
  font-size: 0.83rem;
}

::v-deep .v-input__prepend-inner .v-icon {
  color: #84958d;
  font-size: 19px;
}

::v-deep .v-select__selection {
  color: #344740;
  font-size: 0.83rem;
}

::v-deep .v-input--dense > .v-input__control > .v-input__slot {
  min-height: 44px;
}

::v-deep .v-textarea.v-text-field--outlined.v-input--dense .v-input__control {
  margin-top: 0;
}

::v-deep .v-text-field .v-messages {
  font-size: 0.71rem;
}

/* ================= PETA ================= */

.tt-map-card {
  overflow: hidden;
  border: 1px solid #dfeae5;
  border-radius: 15px;
  background: #ffffff;
}

.tt-map-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 15px 16px;
  background: #f8fbf9;
  border-bottom: 1px solid #e8efeb;
}

.tt-map-toolbar-info {
  flex: 1;
  min-width: 0;
}

.tt-map-toolbar-title {
  display: flex;
  align-items: center;
  color: #29453b;
  font-size: 0.82rem;
  font-weight: 800;
}

.tt-required {
  margin-left: 3px;
  color: #d9534f;
}

.tt-map-toolbar-caption {
  margin-top: 4px;
  color: #85928b;
  font-size: 0.71rem;
  line-height: 1.5;
}

.tt-location-btn {
  flex-shrink: 0;
  min-height: 35px !important;
  border-radius: 9px;
  color: #ffffff !important;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: none;
  letter-spacing: 0;
}

.tt-map-wrap {
  position: relative;
  overflow: hidden;
  border-bottom: 1px solid #e8efeb;
  /* Kurung z-index internal Leaflet agar tidak menembus dialog/elemen lain */
  isolation: isolate;
}

.tt-map {
  width: 100%;
  height: 285px;
  background: #eef3ef;
  z-index: 1;
}

.tt-map-error {
  outline: 2px solid #d9534f;
  outline-offset: -2px;
}

.tt-map-overlay-hint {
  position: absolute;
  z-index: 400;
  top: 12px;
  left: 50%;
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: calc(100% - 24px);
  padding: 9px 13px;
  border: 1px solid rgba(1, 140, 116, 0.12);
  border-radius: 11px;
  background: rgba(255, 255, 255, 0.96);
  color: #426257;
  font-size: 0.74rem;
  font-weight: 700;
  box-shadow: 0 3px 12px rgba(30, 70, 55, 0.08);
  transform: translateX(-50%);
  pointer-events: none;
  white-space: nowrap;
}

.tt-map-hint-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.tt-coordinate-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 35px;
  height: 35px;
  border-radius: 10px;
  background: #f0f6f3;
}

.tt-coordinate-active {
  background: #e1f4ec;
}

.tt-map-footer {
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 13px 15px;
}

.tt-coordinate-content {
  flex: 1;
  min-width: 0;
}

.tt-coordinate-title {
  margin-bottom: 4px;
  color: #3d5148;
  font-size: 0.76rem;
  font-weight: 800;
}

.tt-coordinate-value {
  overflow-wrap: anywhere;
  color: #84918a;
  font-size: 0.71rem;
  line-height: 1.5;
}

.tt-error {
  display: flex;
  align-items: center;
  margin-top: 8px;
  color: #d9534f;
  font-size: 0.75rem;
}

/* ================= UPLOAD FOTO ================= */

.tt-upload-area {
  padding: 16px;
  border: 1px dashed #cddfd6;
  border-radius: 14px;
  background: #fbfdfc;
}

.tt-upload-note {
  display: flex;
  align-items: center;
  margin-top: 8px;
  color: #87938c;
  font-size: 0.71rem;
  line-height: 1.5;
}

.tt-preview-container {
  padding-top: 17px;
  margin-top: 14px;
  border-top: 1px solid #e9f0ec;
}

.tt-preview-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  color: #40574c;
  font-size: 0.77rem;
  font-weight: 800;
}

.tt-preview-count {
  color: #018c74;
  font-size: 0.72rem;
}

.tt-preview-row {
  display: flex;
  flex-wrap: wrap;
  gap: 11px;
}

.tt-preview {
  position: relative;
  width: 86px;
  height: 86px;
  overflow: hidden;
  border: 1px solid #dfe9e3;
  border-radius: 12px;
  background: #edf3ef;
}

.tt-preview img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.tt-preview-index {
  position: absolute;
  bottom: 5px;
  left: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 21px;
  height: 21px;
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 6px;
  background: rgba(1, 90, 74, 0.85);
  color: #ffffff;
  font-size: 0.67rem;
  font-weight: 800;
}

.tt-preview-remove {
  position: absolute;
  top: 5px;
  right: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: 7px;
  background: rgba(35, 50, 44, 0.8);
  cursor: pointer;
}

.tt-preview-remove:hover {
  background: #c74747;
}

/* ================= PENGIRIM ================= */

.tt-sender-note {
  display: flex;
  align-items: flex-start;
  padding: 12px;
  margin-bottom: 16px;
  border: 1px solid #e2eee8;
  border-radius: 10px;
  background: #f6faf7;
  color: #718179;
  font-size: 0.73rem;
  line-height: 1.6;
}

/* ================= FOOTER ================= */

.tt-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 15px 24px;
  background: #ffffff;
  flex-shrink: 0;
}

.tt-footer-note {
  display: flex;
  align-items: center;
  color: #829087;
  font-size: 0.72rem;
  line-height: 1.5;
}

.tt-footer-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 9px;
  flex-shrink: 0;
}

.tt-cancel-btn {
  border-radius: 9px;
  color: #687970 !important;
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: none;
  letter-spacing: 0;
}

.tt-primary-btn {
  min-height: 42px !important;
  padding: 0 20px !important;
  border-radius: 10px;
  background: #018c74 !important;
  color: #ffffff !important;
  font-size: 0.79rem;
  font-weight: 800;
  text-transform: none;
  letter-spacing: 0;
  box-shadow: 0 5px 13px rgba(1, 140, 116, 0.18) !important;
}

.tt-primary-btn:hover {
  background: #017b66 !important;
}

.tt-primary-btn.v-btn--disabled {
  background: #9bc8bd !important;
  color: #ffffff !important;
}

/* ================= ERROR ================= */

.tt-error-alert {
  margin-top: 20px;
  border-radius: 11px;
  font-size: 0.8rem;
}

/* ================= SUKSES ================= */

.tt-success {
  padding: 40px 30px 35px !important;
  text-align: center;
}

.tt-success-illustration {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 105px;
  height: 105px;
  margin: 0 auto 18px;
}

.tt-success-circle {
  position: absolute;
  border-radius: 50%;
}

.tt-success-circle-outer {
  inset: 0;
  background: #eff9f5;
}

.tt-success-circle-inner {
  inset: 10px;
  border: 1px solid #d3ede3;
  background: #e1f4ec;
}

.tt-success-illustration .v-icon {
  position: relative;
  z-index: 1;
}

.tt-success-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 7px 12px;
  margin-bottom: 15px;
  border: 1px solid #d6eee4;
  border-radius: 30px;
  background: #f0f9f5;
  color: #018c74;
  font-size: 0.67rem;
  font-weight: 800;
  letter-spacing: 0.4px;
}

.tt-success-title {
  margin: 0;
  color: #243c33;
  font-size: 1.28rem;
  font-weight: 800;
  line-height: 1.45;
}

.tt-success-text {
  max-width: 460px;
  margin: 12px auto 22px;
  color: #77857d;
  font-size: 0.84rem;
  line-height: 1.8;
}

.tt-status-card {
  display: flex;
  align-items: center;
  gap: 13px;
  max-width: 430px;
  padding: 16px;
  margin: 0 auto 24px;
  border: 1px solid #dcefe6;
  border-radius: 13px;
  background: #f7fbf8;
  text-align: left;
}

.tt-status-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: #e2f3eb;
}

.tt-status-title {
  margin-bottom: 4px;
  color: #315448;
  font-size: 0.83rem;
  font-weight: 800;
}

.tt-status-caption {
  color: #849087;
  font-size: 0.74rem;
  line-height: 1.6;
}

.tt-success-button {
  min-width: 145px;
}

/* ================= HONEYPOT ================= */

.tt-honeypot {
  position: absolute;
  left: -9999px;
  width: 1px;
  height: 1px;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
}

/* ================= RESPONSIVE ================= */

@media (max-width: 600px) {
  .tt-card {
    max-height: 100%;
    height: 100%;
    border-radius: 0 !important;
  }

  .tt-header {
    gap: 11px;
    padding: 17px 16px;
  }

  .tt-header-icon {
    width: 44px;
    height: 44px;
    border-radius: 13px;
  }

  .tt-title {
    font-size: 1.03rem;
  }

  .tt-subtitle {
    font-size: 0.72rem;
  }

  .tt-body {
    padding: 18px 16px 24px !important;
  }

  .tt-info-banner {
    padding: 12px;
    margin-bottom: 23px;
  }

  .tt-section-heading {
    gap: 10px;
  }

  .tt-section-icon {
    width: 38px;
    height: 38px;
  }

  .tt-section-number {
    width: 28px;
    height: 28px;
  }

  .tt-section-divider {
    margin: 23px 0;
  }

  .tt-map-toolbar {
    align-items: flex-start;
    padding: 12px;
  }

  .tt-map-toolbar-caption {
    max-width: 210px;
  }

  .tt-location-btn {
    padding: 0 10px !important;
  }

  .tt-map {
    height: 250px;
  }

  .tt-map-overlay-hint {
    font-size: 0.68rem;
  }

  .tt-upload-area {
    padding: 12px;
  }

  .tt-footer {
    flex-direction: column;
    align-items: stretch;
    gap: 11px;
    padding: 12px 16px;
  }

  .tt-footer-note {
    justify-content: center;
  }

  .tt-footer-actions {
    width: 100%;
    display: grid;
    grid-template-columns: 0.8fr 1.5fr;
  }

  .tt-footer-actions .v-btn {
    margin: 0;
    min-width: 0;
    padding: 0 10px !important;
  }

  .tt-success {
    padding: 35px 20px 25px !important;
  }

  .tt-success-title {
    font-size: 1.12rem;
  }

  .tt-success-text {
    font-size: 0.79rem;
  }
}
</style>

<!--
  Leaflet membuat elemen marker di luar scoped CSS.
  Karena itu, styling marker diletakkan di style non-scoped.
-->
<style>
.tt-pin {
  background: transparent !important;
  border: none !important;
}

.tt-pin-dot {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 3px solid #ffffff;
  border-radius: 50% 50% 50% 4px;
  background: #018c74;
  box-shadow: 0 3px 10px rgba(1, 80, 65, 0.3);
  transform: rotate(-45deg);
}

.tt-pin-dot span {
  display: block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #ffffff;
  transform: rotate(45deg);
}

.tt-dialog-modern {
  overflow: hidden;
  border-radius: 20px;
}

@media (max-width: 600px) {
  .tt-dialog-modern {
    border-radius: 0;
  }
}
</style>
